import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] render crash:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    if (typeof window !== "undefined") window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <div className="max-w-md text-center">
            <h1 className="font-serif text-3xl font-bold mb-3">Something went wrong</h1>
            <p className="font-body text-sm text-muted-foreground mb-6">
              We hit an unexpected error loading this page. Please refresh, or return home.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2 text-sm font-body uppercase tracking-widest border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors rounded-sm"
              >
                Refresh
              </button>
              <button
                onClick={this.handleReset}
                className="px-5 py-2 text-sm font-body uppercase tracking-widest gold-gradient text-foreground rounded-sm"
              >
                Go Home
              </button>
            </div>
            {this.state.error?.message && (
              <pre className="mt-6 text-[11px] text-muted-foreground/70 whitespace-pre-wrap break-words text-left">
                {this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
